import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../api/client";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Users, Car, Calendar, IndianRupee } from "lucide-react";

const COLORS = {
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  approved: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  active: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  confirmed: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  completed: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  rejected: "bg-red-400/10 text-red-300 border-red-400/20",
  cancelled: "bg-slate-400/10 text-slate-300 border-slate-400/20",
  blocked: "bg-red-400/10 text-red-300 border-red-400/20",
  inactive: "bg-slate-400/10 text-slate-300 border-slate-400/20",
  suspended: "bg-red-400/10 text-red-300 border-red-400/20",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, v, c, vh, bk] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { role: "vendor" } }),
        api.get("/admin/users", { params: { role: "customer" } }),
        api.get("/admin/vehicles"),
        api.get("/admin/bookings"),
      ]);
      setStats(s.data); setVendors(v.data); setCustomers(c.data);
      setVehicles(vh.data); setBookings(bk.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const setVendorStatus = async (id, status) => {
    try {
      await api.patch(`/admin/vendors/${id}/status`, { status });
      toast.success(`Vendor ${status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setVehicleStatus = async (id, status) => {
    try {
      await api.patch(`/admin/vehicles/${id}/status`, { status });
      toast.success(`Vehicle ${status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setUserStatus = async (id, status) => {
    try {
      await api.patch(`/admin/users/${id}/status`, { status });
      toast.success(`Customer ${status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Admin · Control room</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-10">RideKaroo overview.</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat icon={<Users className="w-4 h-4" />} label="Customers" value={stats.customers ?? 0} />
          <Stat icon={<Users className="w-4 h-4" />} label="Vendors" value={`${stats.vendors ?? 0}`} note={`${stats.pending_vendors ?? 0} pending`} />
          <Stat icon={<Car className="w-4 h-4" />} label="Vehicles" value={stats.vehicles ?? 0} note={`${stats.pending_vehicles ?? 0} pending`} />
          <Stat icon={<IndianRupee className="w-4 h-4" />} label="Revenue" value={`₹${(stats.revenue ?? 0).toLocaleString()}`} />
        </div>

        <Tabs defaultValue="vendors">
          <TabsList className="bg-[#0A111A] border border-white/5">
            <TabsTrigger value="vendors" data-testid="ad-tab-vendors" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Vendors</TabsTrigger>
            <TabsTrigger value="customers" data-testid="ad-tab-customers" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Customers</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="ad-tab-vehicles" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Vehicles</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="ad-tab-bookings" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Bookings</TabsTrigger>
          </TabsList>

          <TabsContent value="vendors" className="mt-6 space-y-3">
            {vendors.length === 0 ? <Empty label="No vendors yet" /> : vendors.map((v) => (
              <Row key={v.id} testid={`vendor-${v.id}`}>
                <div className="flex-1">
                  <p className="font-medium">{v.name} <span className="text-slate-500 text-sm">· {v.business_name || "—"}</span></p>
                  <p className="text-sm text-slate-400">{v.email} {v.phone && `· ${v.phone}`}</p>
                </div>
                <Badge className={`${COLORS[v.status]} border capitalize`}>{v.status}</Badge>
                <div className="flex gap-2">
                  {v.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => setVendorStatus(v.id, "approved")} data-testid={`approve-vendor-${v.id}`} className="bg-emerald-400 text-[#050B14] hover:bg-emerald-300">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => setVendorStatus(v.id, "rejected")} data-testid={`reject-vendor-${v.id}`} className="border-white/20 bg-transparent text-slate-300">Reject</Button>
                    </>
                  )}
                  {v.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => setVendorStatus(v.id, "suspended")} className="border-white/20 bg-transparent text-slate-300">Suspend</Button>
                  )}
                  {(v.status === "suspended" || v.status === "rejected") && (
                    <Button size="sm" onClick={() => setVendorStatus(v.id, "approved")} className="bg-sky-400 text-[#050B14] hover:bg-sky-300">Reinstate</Button>
                  )}
                </div>
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="customers" className="mt-6 space-y-3">
            {customers.length === 0 ? <Empty label="No customers yet" /> : customers.map((c) => (
              <Row key={c.id} testid={`customer-${c.id}`}>
                <div className="flex-1">
                  <p className="font-medium">{c.name}</p>
                  <p className="text-sm text-slate-400">{c.email}</p>
                </div>
                <Badge className={`${COLORS[c.status] || COLORS.active} border capitalize`}>{c.status}</Badge>
                <Button size="sm" variant="outline"
                  onClick={() => setUserStatus(c.id, c.status === "blocked" ? "active" : "blocked")}
                  className="border-white/20 bg-transparent text-slate-300">
                  {c.status === "blocked" ? "Unblock" : "Block"}
                </Button>
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="vehicles" className="mt-6 space-y-3">
            {vehicles.length === 0 ? <Empty label="No vehicles yet" /> : vehicles.map((v) => (
              <Row key={v.id} testid={`avehicle-${v.id}`}>
                <div className="flex-1">
                  <p className="font-medium">{v.brand} {v.name}</p>
                  <p className="text-sm text-slate-400">By {v.vendor_name} · ₹{v.price_per_day}/day · {v.location}</p>
                </div>
                <Badge className={`${COLORS[v.status]} border capitalize`}>{v.status}</Badge>
                <div className="flex gap-2">
                  {v.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => setVehicleStatus(v.id, "approved")} data-testid={`approve-vehicle-${v.id}`} className="bg-emerald-400 text-[#050B14] hover:bg-emerald-300">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => setVehicleStatus(v.id, "rejected")} data-testid={`reject-vehicle-${v.id}`} className="border-white/20 bg-transparent text-slate-300">Reject</Button>
                    </>
                  )}
                  {v.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => setVehicleStatus(v.id, "inactive")} className="border-white/20 bg-transparent text-slate-300">Deactivate</Button>
                  )}
                  {v.status === "inactive" && (
                    <Button size="sm" onClick={() => setVehicleStatus(v.id, "approved")} className="bg-sky-400 text-[#050B14] hover:bg-sky-300">Reactivate</Button>
                  )}
                </div>
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6 space-y-3">
            {bookings.length === 0 ? <Empty label="No bookings yet" /> : bookings.map((b) => (
              <Row key={b.id} testid={`abooking-${b.id}`}>
                <div className="flex-1">
                  <p className="font-medium">{b.vehicle_name} <span className="text-slate-500 text-sm">· {b.customer_name}</span></p>
                  <p className="text-sm text-slate-400">{b.pickup_location} → {b.drop_location}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{new Date(b.pickup_date).toLocaleDateString()} → {new Date(b.return_date).toLocaleDateString()}</p>
                </div>
                <p className="text-lg text-sky-300">₹{b.total_amount}</p>
                <Badge className={`${COLORS[b.status]} border capitalize`}>{b.status}</Badge>
              </Row>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, note }) {
  return (
    <div className="bg-[#0A111A] border border-white/5 rounded-xl p-5">
      <div className="text-sky-300 mb-3">{icon}</div>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</p>
      <p className="text-3xl font-light tracking-tight">{value}</p>
      {note && <p className="text-xs text-amber-300/80 mt-2">{note}</p>}
    </div>
  );
}
function Row({ children, testid }) {
  return <div data-testid={testid} className="bg-[#0A111A] border border-white/5 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3">{children}</div>;
}
function Empty({ label }) {
  return <div className="bg-[#0A111A] border border-white/5 rounded-xl p-10 text-center text-slate-400">{label}</div>;
}
